<!-- eslint-disable vue/singleline-html-element-content-newline -->
<template>
  <div>
    <Head title="Konfiguracja strony" />
    <h1 class="mb-4 text-3xl font-bold">
      Konfiguracja strony
    </h1>

    <div
      v-if="page?.frontoffice_path"
      class="mb-8 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900"
    >
      <p class="font-medium">
        Ta konfiguracja dotyczy strony frontowej:
      </p>
      <a
        :href="page.frontoffice_path"
        target="_blank"
        rel="noopener noreferrer"
        class="mt-1 inline-block font-mono text-indigo-700 underline hover:text-indigo-900"
      >
        {{ page.frontoffice_path }}
      </a>
      <p v-if="page.page_type === 'resource_show'" class="mt-2 text-xs text-indigo-700/80">
        Podgląd szablonu strony pojedynczej używa pierwszego aktywnego rekordu (po slugu w adresie URL).
      </p>
      <p
        v-if="page.resource_kind === 'article' && page.page_type === 'resource_index'"
        class="mt-2 text-xs text-indigo-700/80"
      >
        Podgląd listy aktualności pokazuje też szkice (oznaczone „Szkic”). Na froncie widoczne są tylko opublikowane wpisy.
      </p>
      <p
        v-if="page.resource_kind === 'article' && page.page_type === 'resource_show'"
        class="mt-2 text-xs text-indigo-700/80"
      >
        Podgląd strony aktualności może używać szkicu. Na froncie widoczne są tylko opublikowane wpisy.
      </p>
    </div>

    <div class="flex min-h-0 gap-4">
      <div class="w-52 shrink-0 overflow-y-auto px-2 pb-6">
        <div
          v-for="kind in orderedKinds"
          :key="kind"
          class="mb-6"
        >
          <h3 class="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">{{ kind }}</h3>
          <div class="space-y-2">
            <div
              v-if="!blocksByKind[kind] || blocksByKind[kind].length === 0"
              class="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center"
            >
              <p class="text-xs text-gray-500">
                Brak dostępnych bloków: {{ kind }}
              </p>
            </div>
            <div
              v-for="block in blocksByKind[kind] || []"
              :key="block.id"
              draggable="true"
              class="group cursor-move rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-all hover:border-indigo-400 hover:bg-indigo-50 hover:shadow-md"
              @dragstart="handleDragStart($event, block)"
            >
              <div class="font-semibold text-gray-900">
                {{ block.name }}
              </div>
              <span
                v-if="block.template?.name"
                class="mt-1.5 inline-block shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
              >
                {{ block.template.name }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="layout-configuration-container flex flex-col overflow-hidden">
        <div class="shrink-0 border-b border-gray-200 px-4 py-3">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold">Układ strony</h2>
            <button
              class="btn-indigo"
              :disabled="processing"
              @click="save"
            >
              Zapisz konfigurację
            </button>
          </div>
        </div>
        <div
          class="min-h-0 flex-1 overflow-y-auto p-3"
          @drop="handleDrop"
          @dragover.prevent="handleDropzoneDragOver"
          @dragenter.prevent="handleDropzoneDragEnter"
          @dragleave="handleDropzoneDragLeave"
        >
          <div
            v-if="layout.length === 0"
            class="flex h-96 items-center justify-center rounded border-2 border-dashed border-gray-300"
          >
            <div class="text-center text-gray-500">
              <p class="text-lg font-medium">Przeciągnij tutaj bloki</p>
              <p class="text-sm">Zacznij budować układ swojej strony</p>
            </div>
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="(item, index) in sortedLayout"
              :key="item.dropzone_id"
              :data-index="index"
              :class="[
                'group relative overflow-hidden rounded-lg border bg-white shadow-sm transition-all hover:shadow-md',
                isLocked(item) ? 'border-indigo-300 bg-indigo-50/30' : 'border-gray-200 hover:border-indigo-300',
                draggedItemIndex === index ? 'border-indigo-400 opacity-50' : ''
              ]"
              @drop="handleItemDrop($event, index)"
              @dragover.prevent="handleItemDragOver($event, index)"
              @dragenter.prevent="handleItemDragEnter($event, index)"
              @dragleave="handleItemDragLeave"
            >
              <div
                v-if="dropIndicatorIndex === index && (draggedItemIndex === null || draggedItemIndex !== index)"
                class="dnd-drop-ghost mb-2 h-12 rounded-lg"
              />
              <div class="flex items-stretch">
                <div
                  :draggable="!isLocked(item)"
                  :class="[
                    'flex items-center border-r px-2',
                    isLocked(item)
                      ? 'cursor-not-allowed border-indigo-200 bg-indigo-50 text-indigo-400'
                      : 'cursor-move border-gray-200 bg-gray-50 hover:bg-gray-100'
                  ]"
                  :title="isLocked(item) ? 'Zablokowany blok systemowy — nie można go przenieść' : 'Przeciągnij, aby zmienić kolejność'"
                  @dragstart="handleItemDragStart($event, index)"
                  @dragend="handleItemDragEnd"
                >
                  <span v-if="isLocked(item)" class="text-base leading-none">🔒</span>
                  <icon
                    v-else
                    name="grip"
                    class="h-4 w-4 fill-gray-400"
                  />
                </div>
                <div class="flex-1">
                  <div
                    :class="[
                      'border-b px-4 py-3',
                      isLocked(item) ? 'border-indigo-200 bg-indigo-50' : 'border-gray-100 bg-gray-50'
                    ]"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="text-sm font-semibold text-gray-800">
                          {{ getItemLabel(item) }}
                        </span>
                        <span
                          v-if="isLocked(item)"
                          class="inline-flex items-center gap-1 shrink-0 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700"
                          title="Ten blok jest przypięty do tej strony zasobu i nie można go usunąć."
                        >
                          🔒 SYSTEM
                        </span>
                        <span
                          v-if="getResolvedBlock(item)?.template?.name"
                          class="inline-block shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                        >
                          {{ getResolvedBlock(item).template.name }}
                        </span>
                      </div>
                      <div class="flex items-center gap-1">
                        <Link
                          v-if="!isLocked(item) && getResolvedBlock(item)?.id"
                          :href="editBlockRoute(getResolvedBlock(item))"
                          class="btn-action-edit"
                          title="Edytuj"
                        >
                          <icon name="edit" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                          Edytuj
                        </Link>
                        <button
                          v-if="!isLocked(item)"
                          class="btn-action-delete"
                          title="Usuń"
                          @click="removeItem(index)"
                        >
                          <icon name="trash" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                          Usuń
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="p-2">
                    <block-preview compact constrained :block="getResolvedBlock(item)" />
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="dropIndicatorIndex === sortedLayout.length"
              class="dnd-drop-ghost h-12 rounded-lg"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import { v4 as uuidv4 } from 'uuid';
import Icon from '@/Shared/Icon.vue';
import BlockPreview from '@/Shared/BlockPreview.vue';
import { BLOCK_CATEGORY_ORDER } from '@/config/blockCategories';
import { createLayoutHelper } from '@/utils/layoutHelper';
import { getEditBlockRoute } from '@/utils/createBlockRouteHelper';

export default {
  components: {
    Head,
    Link,
    Icon,
    BlockPreview,
  },
  layout: createLayoutHelper,
  props: {
    pageConfiguration: {
      type: Object,
      required: true,
    },
    blocksByKind: {
      type: Object,
      required: true,
    },
    // System blocks currently embedded in the layout, keyed by id. NOT shown
    // in the picker (admins can't insert them manually); rendered here so
    // their cards display nicely with template/preview.
    systemBlocksById: {
      type: Object,
      default: () => ({}),
    },
    updatePath: {
      type: String,
      required: true,
    },
    page: {
      type: Object,
      default: null,
    },
    // eslint-disable-next-line vue/prop-name-casing
    site_context: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      layout: this.normalizeLayout(this.pageConfiguration.layout),
      draggedBlock: null,
      draggedItemIndex: null,
      dropIndicatorIndex: null,
      processing: false,
    };
  },
  watch: {
    'pageConfiguration.layout': {
      handler(layout) {
        this.layout = this.normalizeLayout(layout);
      },
      deep: true,
    },
  },
  computed: {
    sortedLayout() {
      return [...this.layout].sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    orderedKinds() {
      return BLOCK_CATEGORY_ORDER;
    },
  },
  methods: {
    editBlockRoute(block) {
      return getEditBlockRoute(
        this.$routes,
        block.site_context ?? this.site_context,
        block.id,
        block.kind,
      );
    },
    normalizeLayout(layout) {
      return (layout || []).map((item, index) => ({
        ...item,
        dropzone_id: item.dropzone_id || uuidv4(),
        order: item.order ?? index,
      }));
    },
    handleDragStart(event, block) {
      this.draggedBlock = block;
      this.draggedItemIndex = null;
      event.dataTransfer.effectAllowed = 'move';
    },
    handleDrop(event) {
      event.preventDefault();
      this.dropIndicatorIndex = null;

      if (!this.draggedBlock) return;

      const dropIndex = this.getDropIndex(event);

      const newItem = {
        dropzone_id: uuidv4(),
        type: 'block',
        block_id: this.draggedBlock.id,
        order: dropIndex,
      };

      const sortedItems = [...this.sortedLayout];
      sortedItems.splice(dropIndex, 0, newItem);

      this.layout = sortedItems.map((item, index) => ({ ...item, order: index }));
      this.draggedBlock = null;
    },
    handleItemDragStart(event, index) {
      if (this.isLocked(this.sortedLayout[index])) {
        event.preventDefault();
        return;
      }
      this.draggedItemIndex = index;
      this.draggedBlock = null;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', index.toString());

      const itemElement = event.currentTarget.closest('[data-index]');
      if (itemElement) {
        const dragImage = itemElement.cloneNode(true);
        dragImage.style.width = `${itemElement.offsetWidth}px`;
        dragImage.style.opacity = '0.8';
        dragImage.style.transform = 'rotate(2deg)';
        dragImage.style.pointerEvents = 'none';
        dragImage.style.position = 'fixed';
        dragImage.style.left = '-9999px';
        dragImage.style.top = '0px';
        dragImage.style.zIndex = '9999';
        document.body.appendChild(dragImage);

        dragImage.offsetHeight;

        const rect = itemElement.getBoundingClientRect();
        const handleRect = event.target.closest('.cursor-move')?.getBoundingClientRect();
        const offsetX = handleRect ? event.clientX - rect.left : 20;
        const offsetY = event.clientY - rect.top;

        event.dataTransfer.setDragImage(dragImage, offsetX, offsetY);

        requestAnimationFrame(() => {
          if (dragImage.parentNode) dragImage.parentNode.removeChild(dragImage);
        });
      }
    },
    handleItemDragEnd() {
      this.draggedItemIndex = null;
      this.dropIndicatorIndex = null;
    },
    handleItemDrop(event, targetIndex) {
      event.preventDefault();
      event.stopPropagation();
      this.dropIndicatorIndex = null;

      if (this.draggedBlock && this.draggedItemIndex === null) {
        const rect = event.currentTarget.getBoundingClientRect();
        const mouseY = event.clientY;
        const itemCenterY = rect.top + rect.height / 2;

        const insertIndex = mouseY < itemCenterY ? targetIndex : targetIndex + 1;

        const newItem = {
          dropzone_id: uuidv4(),
          type: 'block',
          block_id: this.draggedBlock.id,
          order: insertIndex,
        };

        const sortedItems = [...this.sortedLayout];
        sortedItems.splice(insertIndex, 0, newItem);

        this.layout = sortedItems.map((item, index) => ({ ...item, order: index }));

        this.draggedBlock = null;
        return;
      }

      if (this.draggedItemIndex === null) return;

      const fromIndex = this.draggedItemIndex;
      if (fromIndex === targetIndex) {
        this.draggedItemIndex = null;
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const mouseY = event.clientY;
      const itemCenterY = rect.top + rect.height / 2;

      // `insertIndex` is the slot in the pre-removal array where the item should
      // land. `moveItem` already compensates for the gap left by the dragged
      // item, so we must NOT subtract here too (double adjustment was the cause
      // of downward moves landing one slot too high).
      const insertIndex = mouseY < itemCenterY ? targetIndex : targetIndex + 1;

      this.moveItem(fromIndex, insertIndex);
      this.draggedItemIndex = null;
    },
    handleItemDragOver(event, index) {
      const rect = event.currentTarget.getBoundingClientRect();
      const mouseY = event.clientY;
      const itemCenterY = rect.top + rect.height / 2;

      if (this.draggedBlock && this.draggedItemIndex === null) {
        this.dropIndicatorIndex = mouseY < itemCenterY ? index : index + 1;
      } else if (this.draggedItemIndex !== null) {
        this.dropIndicatorIndex = mouseY < itemCenterY ? index : index + 1;
      }
    },
    handleItemDragEnter(event, index) {
      const rect = event.currentTarget.getBoundingClientRect();
      const mouseY = event.clientY;
      const itemCenterY = rect.top + rect.height / 2;

      if (this.draggedBlock && this.draggedItemIndex === null) {
        this.dropIndicatorIndex = mouseY < itemCenterY ? index : index + 1;
      } else if (this.draggedItemIndex !== null) {
        this.dropIndicatorIndex = mouseY < itemCenterY ? index : index + 1;
      }
    },
    handleItemDragLeave() {},
    handleDropzoneDragOver(event) {
      if (this.draggedBlock && this.draggedItemIndex === null) {
        this.dropIndicatorIndex = this.getDropIndex(event);
      }
    },
    handleDropzoneDragEnter(event) {
      if (this.draggedBlock && this.draggedItemIndex === null) {
        this.dropIndicatorIndex = this.getDropIndex(event);
      }
    },
    handleDropzoneDragLeave(event) {
      const relatedTarget = event.relatedTarget;
      if (!relatedTarget || !event.currentTarget.contains(relatedTarget)) {
        this.dropIndicatorIndex = null;
      }
    },
    getDropIndex(event) {
      const dropzone = event.currentTarget;
      const children = Array.from(dropzone.querySelectorAll('[data-index]'));

      if (children.length === 0) return 0;

      const mouseY = event.clientY;
      let dropIndex = 0;

      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const rect = child.getBoundingClientRect();

        if (mouseY < rect.top + rect.height / 2) {
          dropIndex = i;
          break;
        }

        if (i === children.length - 1) dropIndex = children.length;
      }

      return dropIndex;
    },
    isLocked(item) {
      return !!item.locked;
    },
    removeItem(index) {
      const item = this.sortedLayout[index];
      if (this.isLocked(item)) return;

      const sorted = [...this.sortedLayout];
      sorted.splice(index, 1);
      this.layout = sorted.map((layoutItem, i) => ({ ...layoutItem, order: i }));
      this.save();
    },
    moveItem(fromIndex, toIndex) {
      const sortedItems = [...this.sortedLayout];
      const adjustedToIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      const itemToMove = sortedItems[fromIndex];
      sortedItems.splice(fromIndex, 1);
      sortedItems.splice(adjustedToIndex, 0, itemToMove);

      this.layout = sortedItems.map((item, index) => ({ ...item, order: index }));
    },
    reorderLayout() {
      this.layout = this.layout.map((item, index) => ({ ...item, order: index }));
    },
    blockIdFor(item) {
      return item.block_id ?? item.id;
    },
    getResolvedBlock(item) {
      if (item.type !== 'block' && item.type !== 'component') return null;

      const targetId = this.blockIdFor(item);

      // 1) user blocks (visible in the picker)
      for (const blocks of Object.values(this.blocksByKind)) {
        const found = blocks.find((b) => b.id === targetId);
        if (found) {
          return { ...found, order: item.order, dropzone_id: item.dropzone_id };
        }
      }

      // 2) system blocks (locked, never in the picker)
      const sys = this.systemBlocksById[String(targetId)] || this.systemBlocksById[targetId];
      if (sys) {
        return { ...sys, order: item.order, dropzone_id: item.dropzone_id };
      }
      return null;
    },
    getItemLabel(item) {
      const resolved = this.getResolvedBlock(item);
      if (resolved) return resolved.name;
      return 'Nieznany';
    },
    save() {
      this.reorderLayout();
      this.processing = true;
      this.$inertia.put(
        this.updatePath,
        { layout: this.layout },
        {
          preserveScroll: true,
          onFinish: () => { this.processing = false; },
        },
      );
    },
  },
};
</script>
